import React, {CSSProperties, memo, useContext, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import {DfpSizeMapping} from '@/app/interfaces/ad';
import {useLazyLoadingIntersectionChecker} from '@/common/helpers/hooks/useLazyLoadingIntersectionChecker';
import {sendStats} from '@/common/helpers/stats/sendStats';
import {useScriptInjector} from '@/common/services/headTagger';
import {RenderConditionContext} from '@/app/contexts/RenderConditionContext';
import {useAdRefresh} from '@/app/components/common/advertising';
import {useBannerViewability} from '@/app/components/common/advertising/useBannerViewability';
import {DfpAdContext} from '../DfpAdProvider';
import {useDfpAdBannerAnalytics} from './useDfpAdBannerAnalytics';
import styles from './DfpAdBanner.css';

interface DfpAdBannerProps {
	className?: string;
	unitPath: string;
	divId: string;
	sizes: [number, number][];
	sizeMapping?: DfpSizeMapping;
	collapseEmpty?: boolean;
	reserveSize?: boolean | 'width' | 'height';
	lazyLoading?: boolean;
	isBranding?: boolean;
	forceSafeFrame?: boolean;
	analyticsLabel: string;
	interfaceName: string;
	placeholder?: boolean;
	extraScripts?: {src: string; async?: boolean}[];
	forceMinSize?: [number, number];
}

export const DfpAdBanner = memo<DfpAdBannerProps>(props => {
	const {
		className,
		unitPath,
		divId,
		sizes,
		sizeMapping,
		reserveSize,
		lazyLoading,
		isBranding,
		forceSafeFrame,
		analyticsLabel,
		interfaceName,
		placeholder,
		extraScripts,
		forceMinSize,
	} = props;

	const {serviceEnabled} = useContext(DfpAdContext)!;
	const renderConditionRef = useContext(RenderConditionContext);
	const isInitialRender = useMemo(() => renderConditionRef.current.isInitial, []);

	const [placeholderShown, setPlaceholderShown] = useState(!!placeholder);

	const style: CSSProperties = {};
	if ((reserveSize === true || reserveSize === 'width') && !forceMinSize) {
		style.width = sizes[0][0];
	}
	if ((reserveSize === true || reserveSize === 'height') && !forceMinSize) {
		style.height = sizes[0][1];
	}

	if (placeholderShown) {
		style.minWidth = Math.min(...sizes.map(([width]) => width));
		style.minHeight = Math.min(...sizes.map(([, height]) => height));
	}

	if (forceMinSize) {
		style.minWidth = forceMinSize[0];
		style.minHeight = forceMinSize[1];
	}

	// Lazy loading logic
	const blockRef = useRef<HTMLDivElement>(null);
	const [intersecting, setIntersecting] = useState(false);
	const intersectionChecker = useLazyLoadingIntersectionChecker({
		ref: blockRef,
		isActive: !!lazyLoading,
		advance: 50,
	});
	const isViewableEnoughRef = useBannerViewability(blockRef, interfaceName);
	const showAllowed = serviceEnabled && (!lazyLoading || intersecting);

	const slotRef = useRef<googletag.Slot | null>(null);

	const sendDisplayStat = useDfpAdBannerAnalytics({
		showAllowed,
		unitPath,
		isInitialRender,
		interfaceName,
		analyticsLabel,
	});

	useEffect(() => {
		if (!showAllowed) {
			return;
		}

		sendDisplayStat('display');
	}, [sendDisplayStat, showAllowed]);

	// Remove global defined branding styles
	const cleanUpBranding = () => {
		if (!isBranding) {
			return;
		}

		const brandingStyle = document.getElementById('branding_style');
		const brandingScript = document.getElementById('branding_js');
		if (brandingStyle) {
			brandingStyle.remove();
		}
		if (brandingScript) {
			brandingScript.remove();
		}
	};

	// Add logic of the slot refreshing
	useAdRefresh(() => {
		if (!isViewableEnoughRef.current) {
			return;
		}

		if (serviceEnabled && googletag.apiReady && slotRef.current) {
			cleanUpBranding();
			sendDisplayStat('refresh');
			googletag.pubads().refresh([slotRef.current]);
			sendStats(`ad/${analyticsLabel}/display/am${unitPath}`);
		}
	}, [serviceEnabled, sendDisplayStat]);

	// Check intersecting for lazy loading banners
	useEffect(() => {
		if (!lazyLoading || intersecting) {
			return;
		}

		if (intersectionChecker.check()) {
			setIntersecting(true);
			return;
		}

		return intersectionChecker.observe(() => {
			setIntersecting(true);
		});
	}, [lazyLoading, intersecting]);

	// Register the slot
	useEffect(() => {
		// When lazy loading is enabled first of all check is the banner block intersecting the viewport
		if (lazyLoading && !intersecting) {
			return;
		}

		const slotDefineCommand = () => {
			// Filter out sizes wider than the current viewport to prevent banner overflow
			const filteredSizes = sizes.filter(([width]) => width <= window.innerWidth);
			const effectiveSizes = filteredSizes.length > 0 ? filteredSizes : sizes;

			const slot = googletag.defineSlot(
				unitPath,
				effectiveSizes.length === 1 ? effectiveSizes[0] : effectiveSizes,
				divId,
			);
			if (!slot) {
				return;
			}
			slot.addService(googletag.pubads());

			// Set slot-level force safe frame config
			if (forceSafeFrame !== undefined) {
				slot.setConfig({
					safeFrame: forceSafeFrame
						? {
								forceSafeFrame: true,
								allowOverlayExpansion: true,
								allowPushExpansion: true,
							}
						: {
								forceSafeFrame: false,
							},
				});
			}

			if (sizeMapping) {
				const mapping = googletag.sizeMapping();
				for (const [viewportSize, slotSize] of sizeMapping) {
					mapping.addSize(viewportSize, slotSize);
				}
				const builtMapping = mapping.build();
				if (builtMapping) {
					slot.defineSizeMapping(builtMapping);
				}
			}

			slotRef.current = slot;
		};

		googletag.cmd.push(slotDefineCommand);

		// Destroy the slot
		return () => {
			if (!googletag.apiReady && Array.isArray(googletag.cmd)) {
				const pendingCommands = googletag.cmd as (() => void)[];
				const index = pendingCommands.indexOf(slotDefineCommand);
				if (index !== -1) {
					pendingCommands.splice(index, 1);
				}
				return;
			}

			if (!slotRef.current) {
				return;
			}
			googletag.destroySlots([slotRef.current]);

			cleanUpBranding();
		};
	}, [unitPath, divId, intersecting]);

	// Show the slot when service is enabled
	useEffect(() => {
		if (!showAllowed) {
			return;
		}

		googletag.cmd.push(() => {
			googletag.display(slotRef.current!);
		});

		// Send old analytics
		sendStats(`ad/${analyticsLabel}/display/am${unitPath}`);

		const handleSlotRendered = (event: googletag.events.SlotRenderEndedEvent) => {
			if (unitPath === event.slot.getAdUnitPath()) {
				sendStats(`ad/${analyticsLabel}/shown/${event.isEmpty ? 'empty' : 'filled'}/am${unitPath}`);
			}
		};

		googletag.pubads().addEventListener('slotRenderEnded', handleSlotRendered);

		return () => {
			googletag.pubads().removeEventListener('slotRenderEnded', handleSlotRendered);
		};
	}, [showAllowed]);

	// Listen slot render to unmount placeholder
	useEffect(() => {
		if (!serviceEnabled || !placeholderShown) {
			return;
		}

		const slotRenderedHandler = (event: googletag.events.SlotRenderEndedEvent) => {
			if (event.slot.getAdUnitPath() === unitPath) {
				setPlaceholderShown(false);
			}
		};
		googletag.pubads().addEventListener('slotRenderEnded', slotRenderedHandler);

		return () => {
			googletag.pubads().removeEventListener('slotRenderEnded', slotRenderedHandler);
		};
	}, [serviceEnabled, unitPath]);

	// Inject extra scripts
	if (extraScripts) {
		for (const item of extraScripts) {
			useScriptInjector(item);
		}
	}

	return (
		<div
			ref={blockRef}
			className={cn(styles.root, placeholderShown && styles.withPlaceholder, className)}
			id={divId}
			style={style}
		/>
	);
});

DfpAdBanner.displayName = 'DfpAdBanner';
